import { getRequestHeader, getResponseHeader, type H3Event, type RequestHeaders } from 'h3'
import { defu } from 'defu'
import type { ServiceOptions } from '../types/services'
import { splitSetCookieString, parseSetCookie, serialize as serializeCookie, parse as parseCookies } from 'cookie-es'

export interface BuildApiRequestHeadersOptions {
    /**
     * @description The H3 request event
     */
    event: H3Event
    /**
     * @description The incoming request headers (e.g. from `useRequestHeaders()` in the app, or h3 `getRequestHeaders(event)` in a Nitro route)
     */
    requestHeaders: RequestHeaders
    /**
     * @description A Record of extra headers to add besides the dynamically built headers
     */
    extraHeaders?: Record<string, any>
    /**
     * @description An optional service config
     */
    service?: ServiceOptions
}

const IGNORE_PROXY_HEADERS = [
    'cf-connecting-ip',
    'cf-ray',
    'connection',
    'content-length',
    'content-md5',
    'content-type',
    'host',
    'x-forwarded-host',
    'x-forwarded-port',
    'x-forwarded-proto',
] as const

/**
 * @title buildApiRequestHeaders
 * @description
 * Generates the Header object for API requests when using `useApi` or derivative functions.
 * It manages cookies to ensure consistency when making multiple API calls on the serverside.
 *
 * @example
 *
 * ```ts
 * const api = useApi()
 *
 * // Server: Make an API request, which returns cookie `x` with a unique value
 * const responseA = await api("/url-that-returns-cookie-x")
 * // Server: Make subsequent request to an endpoint that also returns cookie `x` with a new unique value, unless an existing cookie `x` is included in the request
 * const responseB = await api("/url-that-also-returns-cookie-x")
 *
 * // Because we are using useApi here, the cookie `x` we get return in responseA will be automatically included in the subsequent request for responseB, so responseB don't force a new value.
 * // Usually the browser manages this in SPA, but in Nuxt we need to manage it manually while we are on the server.
 * ```
 * @returns A Headers object
 */
export const buildApiRequestHeaders = (options: BuildApiRequestHeadersOptions): Headers => {
    const { event, requestHeaders, extraHeaders = {}, service } = options

    const rawHeaders: Record<string, string> = defu(extraHeaders, {
        ...requestHeaders,
        'x-requested-with': 'XMLHttpRequest',
        'accept': 'application/json',
    } as Record<string, string>)

    if (import.meta.server) {
        const rawResponseCookies = getResponseHeader(event, 'set-cookie') as string | string[]
        const requestCookieHeader = rawHeaders.cookie ? (getRequestHeader(event, 'cookie') || '') : ''

        const mergedCookie = mergeServerRequestCookies(rawResponseCookies, requestCookieHeader)

        // Carry cookies set by earlier serverside API calls into this request (the browser does this automatically in an SPA).
        if (mergedCookie !== null) {
            rawHeaders.cookie = mergedCookie
        }
    }

    const headers = new Headers(rawHeaders)

    // Remove proxy headers
    for (const header of IGNORE_PROXY_HEADERS) {
        if (import.meta.server) {
            if (header === 'cf-connecting-ip' && service?.server?.forward?.cfConnectingIp) continue
        }

        headers.delete(header)
    }

    return headers
}

/**
 * Builds the outgoing Cookie header from the response's Set-Cookie headers plus the
 * browser's request cookies; response cookies win (they're newer). Returns null when the
 * response set none, meaning "leave the header as-is". Pure (no h3/import.meta) so it's unit-testable.
 */
export function mergeServerRequestCookies(
    rawResponseCookies: string | string[],
    requestCookieHeader: string,
): string | null {
    // Node may hold multiple Set-Cookie headers as an array; collapse to one string.
    const responseCookieString = Array.isArray(rawResponseCookies)
        ? rawResponseCookies.join(';')
        : rawResponseCookies

    const responseCookies = splitSetCookieString(responseCookieString)

    if (!responseCookies.length) {
        return null
    }

    const requestCookies = parseCookies(requestCookieHeader)
    const presentCookies = new Set<string>()
    const mergedCookies: string[] = []

    for (const cookieString of responseCookies) {
        const responseCookie = parseSetCookie(cookieString)

        // parseSetCookie returns undefined for unparseable strings.
        if (!responseCookie) {
            continue
        }

        mergedCookies.push(
            serializeCookie(responseCookie.name, responseCookie.value, {
                domain: responseCookie.domain,
                expires: responseCookie.expires,
                httpOnly: responseCookie.httpOnly,
                maxAge: responseCookie.maxAge,
                path: responseCookie.path,
                sameSite: responseCookie.sameSite,
                secure: responseCookie.secure,
            })
        )

        presentCookies.add(responseCookie.name)
    }

    // Append browser cookies that the response did not already set.
    for (const [name, value] of Object.entries(requestCookies)) {
        if (presentCookies.has(name)) {
            continue
        }

        mergedCookies.push(`${name}=${value}`)
        presentCookies.add(name)
    }

    return mergedCookies.join('; ')
}
